import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Modal } from "./Modal";
import { GhostButton, ActionButton, inputClass } from "./Primitives";
import {
  KPI_CATALOG,
  DEFAULT_VISIBLE_BY_ROLE,
  SECTION_ORDER,
  isKpiAllowedForRole,
} from "../lib/dashboardPrefs";

/**
 * DashboardCustomizeModal — twin-purpose modal for the admin dashboard:
 *   1. Toggle which KPI tiles are visible (checkboxes grouped by section)
 *   2. Rename any of the three section headers (Overview / Personnel /
 *      Operational) — persisted in the same prefs blob.
 */
export default function DashboardCustomizeModal({
  open,
  onClose,
  role,
  visible,
  sectionLabels = {},
  onSave,
}) {
  const [selected, setSelected] = useState(visible);
  const [labels, setLabels]     = useState(sectionLabels);

  // Sync local state whenever the modal opens with fresh prefs.
  useEffect(() => {
    if (!open) return;
    setSelected(visible);
    setLabels(sectionLabels);
  }, [visible, sectionLabels, open]);

  const groups = useMemo(() => {
    const bySection = {};
    Object.entries(KPI_CATALOG).forEach(([id, meta]) => {
      if (!isKpiAllowedForRole(id, role)) return;
      (bySection[meta.section] ||= []).push({ id, ...meta });
    });
    return bySection;
  }, [role]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const setLabel = (sectionKey, value) => {
    setLabels((prev) => ({ ...prev, [sectionKey]: value }));
  };

  const restoreDefaults = () => {
    setSelected((DEFAULT_VISIBLE_BY_ROLE[role] || DEFAULT_VISIBLE_BY_ROLE.admin).filter(
      (id) => isKpiAllowedForRole(id, role),
    ));
    setLabels({});
  };

  const save = () => {
    // Drop blank/whitespace-only labels so the default label kicks back in.
    const cleaned = Object.fromEntries(
      Object.entries(labels)
        .filter(([, v]) => v && String(v).trim())
        .map(([k, v]) => [k, String(v).trim()]),
    );
    onSave({ visible: selected, sectionLabels: cleaned });
    toast.success("Dashboard preferences saved.");
    onClose();
  };

  return (
    <Modal onClose={onClose} title="Customize dashboard" testId="dashboard-customize-modal">
      <div className="space-y-6">
        <p className="text-zinc-400 text-sm">
          Pick which KPI tiles show, and rename the section headers to match how your team talks.
          Prefs are saved to this browser only.
        </p>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
          {/* Section label editor */}
          <div data-testid="dashboard-customize-labels">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="label-mute">Section labels</div>
              {Object.values(labels).some((v) => v && String(v).trim()) && (
                <button
                  type="button"
                  data-testid="dashboard-customize-labels-reset"
                  onClick={() => setLabels({})}
                  className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#FF3B30] transition-colors"
                >
                  Reset section names
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {SECTION_ORDER.map((sectionKey) => (
                <label
                  key={sectionKey}
                  className="border border-[#27272A] bg-[#0A0A0A] p-3 flex flex-col gap-1"
                >
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                    Default: {sectionKey}
                  </span>
                  <input
                    className={inputClass}
                    data-testid={`dashboard-customize-label-${sectionKey.toLowerCase()}`}
                    value={labels[sectionKey] ?? ""}
                    onChange={(e) => setLabel(sectionKey, e.target.value)}
                    placeholder={sectionKey}
                    maxLength={40}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Tile toggles grouped by (catalog-default) section */}
          {Object.entries(groups).map(([section, items]) => (
            <div key={section}>
              <div className="label-mute mb-2">
                {(labels[section]?.trim()) || section}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((item) => {
                  const on = selected.includes(item.id);
                  return (
                    <label
                      key={item.id}
                      data-testid={`dashboard-customize-option-${item.id}`}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors ${
                        on
                          ? "border-[#FF3B30] bg-[#FF3B30]/5 text-white"
                          : "border-[#27272A] bg-[#0A0A0A] text-zinc-400 hover:border-[#3F3F46]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(item.id)}
                        className="accent-[#FF3B30]"
                      />
                      <span className="text-sm">{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-[#27272A]">
          <GhostButton onClick={restoreDefaults} data-testid="dashboard-customize-restore">
            Restore defaults
          </GhostButton>
          <div className="flex items-center gap-2">
            <GhostButton onClick={onClose} data-testid="dashboard-customize-cancel">
              Cancel
            </GhostButton>
            <ActionButton onClick={save} data-testid="dashboard-customize-save">
              Save preferences
            </ActionButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}
